/**
 * Pike's spray bay — garage mode. Drive slowly into the marked bay at a respray shop (src/garage/bay.ts)
 * and the car stops, the HUD and touch controls step aside, the camera comes down to the car (framing.ts:
 * each category of the menu has its own shot, auto-fitted to the car and the free part of the screen; drag /
 * swipe to look round, pinch or wheel to zoom) and a slim control strip (garageModeScreen.ts) tries paint,
 * finish, rims, rim colour, a neon underglow and the stance (ride height, camber, track) on the car live.
 * "Pay & drive out" pays the bill; "Leave" puts everything back.
 *
 *  - Paint is the respray: priced by the wanted level and with its rules (repaired, new plates, heat off).
 *  - Where your car lives: offline the save keeps ONE car (the activities' "motor", the same build sheet as
 *    Halloran's) and, with the lockup key, Lockup 7 brings it out. Work on a car that isn't yours is allowed
 *    but not saved; "Keep this one" (KEEP_PRICE) makes it your car. Online the server keeps owned cars and
 *    their mods (src/online-ui/sprayBayOnline.ts is the backend, server/economy/garage.ts validates).
 *  - Not while the police can see you (heat.seen); with heat but out of sight, a respray takes it off.
 *
 * Only active in garage mode: the trigger is a cheap rectangle test per fixed step; the camera override,
 * the input listeners and the screen exist only while the bay is open. No per-frame allocations.
 */
import type { GameContext } from '../core/context';
import type { System } from '../core/system';
import { canWorkOn } from '../activities/garage';
import type { CarBuild } from '../activities/state';
import type { CameraOverride } from '../camera/cameraRig';
import { LOCKUP_UNLOCK_ID, resprayCost } from '../economy/wallet';
import { touchUi } from '../input/touch';
import { CAR_LOOK_KEY, type CarLook } from '../vehicles/carTraits';
import type { Vehicle } from '../vehicles/types';
import { workLight } from '../render/renderSystem';
import { KEEP_PRICE, modLines, stockMods, type CarMods, type ModKey } from '../../shared/garage';
import { discounted } from '../../shared/contacts';
import { contactPerk, contactPrice } from '../contacts/perks';
import { bayFor, inBay, type SprayBay } from './bay';
import { lookWith, modsFromLook, putLook } from './look';
import { requestRimModels, rimGeometry } from '../vehicles/rimModels';
import { buildBayViews, type BayViews } from './bayView';
import { frameShot, nearestTurn, SHOTS, yawFor, type FrameIn, type FrameOut, type GarageView } from './framing';
import { GarageModeScreen, type BayController } from './garageModeScreen';
import { buildStudio, type Studio } from './studio';
import { sceneOverride } from '../render/sceneOverride';
import { vehicleSolo } from '../vehicles/vehicleView';

/** What the player is buying: paint (null = unchanged), the mods as they should be, and whether to keep the car. */
export interface SprayBayOrder {
  paint: number | null;
  mods: CarMods;
  keep: boolean;
  /** Pounds, as the menu showed them (the server prices online on its own). */
  paintCost: number;
  modsCost: number;
  keepCost: number;
  /** The gang livery going on or off (online only, WP-X4): Pike's fee, 0 when it doesn't change. */
  liveryCost: number;
  total: number;
}

/** Online: the server pays and keeps (src/online-ui/sprayBayOnline.ts). Offline: none (the wallet and the save). */
export interface SprayBayBackend {
  balance(): number;
  paintCost(): number;
  /** Why the bay won't take this car online (not networked, not the driver…), or null. */
  refuse(v: Vehicle): string | null;
  /** Your owned car? (Online: owned on the server.) */
  isMine(v: Vehicle): boolean;
  /** Where your car is kept (the line in the menu). */
  keptAt(): string;
  /** The car's mods as the server knows them. */
  modsOf(v: Vehicle): CarMods;
  /**
   * The gang livery Pike can put on this car (online, WP-X4: your gang has bought it, the car is yours and has a
   * trim): its colour and Pike's fees. Null: no livery offered (the category isn't shown).
   */
  livery?(v: Vehicle): { colour: number; fee: number; off: number } | null;
  pay(v: Vehicle, order: SprayBayOrder): Promise<{ ok: boolean; reason?: string }>;
}

export interface SprayBayService {
  readonly isOpen: boolean;
  /** Open garage mode for the car you drive at this shop's bay (E at the shop). False (with a toast) if not. */
  open(poiId?: string): boolean;
  /** The shop's E prompt. */
  prompt(poiId: string): string;
  setBackend(b: SprayBayBackend | null): void;
}

declare module '../core/services' {
  interface ServiceMap {
    sprayBay: SprayBayService;
  }
}

declare module '../core/events' {
  interface GameEvents {
    /** Garage mode opened / closed at a spray bay (src/garage). */
    sprayBayOpened: { vehicleId: number; poi: string };
    sprayBayClosed: { vehicleId: number; paid: number };
  }
}

declare module '../core/hooks' {
  interface BhHooks {
    /** Spray bay read-out: open, the bay's rectangle, what's tried on, the bill (tests, e2e). */
    sprayBayState(): Record<string, unknown>;
    /** Try something on in garage mode (the menu's own actions): key = paint | finish | wheels | rim | neon | ride | camber | track | keep. */
    sprayBaySet(key: string, value: unknown): boolean;
    /** Put a look (carTraits CarLook: wheels, rim, neon, ride, camber, track…) on any car, or take it off (null). */
    setVehicleLook(vehicleId: number, look: Record<string, unknown> | null): boolean;
    /** Whether the premium rims' pack (rimModels.ts) is in. */
    rimModelsLoaded(): boolean;
    /** Pay & drive out (true when paid or nothing to pay). */
    sprayBayPay(): Promise<{ ok: boolean; reason: string | null; paid: number }>;
    /** Leave without changes. */
    sprayBayLeave(): void;
    /** Orbit the garage camera (radians, degrees, zoom factor). */
    sprayBayOrbit(dYaw: number, dPitch: number, zoom?: number): void;
    /** Point the garage camera at a category's shot (what the menu does when you pick a category). */
    sprayBayView(view: GarageView): void;
  }
}

/** Slower than this (m/s) counts as "driving in". */
const ENTER_SPEED = 3.2;
const PITCH_MIN = 4;
const PITCH_MAX = 72;
/** Zoom is a multiplier on the auto-framed distance (1 = the car fills the free part of the screen). */
const ZOOM_MIN = 0.45;
const ZOOM_MAX = 2.4;
/** The free part of the screen when there is no screen (headless): the strip's usual share. */
const NO_INSETS = { l: 0, r: 0, t: 0.06, b: 0.26 };

const PIKE_LINES = ['"Shutter\'s down. Pick your colours."', '"Don\'t look, don\'t ask. What\'ll it be?"', '"Nice motor. Could be nicer."'];

export function createSprayBaySystem(): System {
  let ctx: GameContext;
  let bays: SprayBay[] = [];
  let views: BayViews | null = null;
  let backend: SprayBayBackend | null = null;
  /** Per bay: may driving in open it (false until you've been outside it since it last closed). */
  const armed = new Map<string, boolean>();

  // ---------------------------------------------------------------- the session
  /** Garage mode is on (with or without its screen: a headless game runs the same session for tests). */
  let active = false;
  let screen: GarageModeScreen | null = null;
  let bay: SprayBay | null = null;
  let car: Vehicle | null = null;
  let before: { paint: number; look: CarLook | undefined } | null = null;
  let current: CarMods = stockMods();
  let pending: CarMods = stockMods();
  let pendingPaint: number | null = null;
  let keep = false;
  let hudHidden = false;
  /** The dark studio the car is shown in while the bay is open (with a renderer only). */
  let studio: Studio | null = null;
  let paying = false;
  let note = '';
  const orbit = { yaw: 0, pitch: SHOTS.paint.pitch, zoom: 1 };
  /** The category's shot the camera is on; `glide` = easing to it (slow), cleared when you drag. */
  let view: GarageView = 'paint';
  let glide = false;
  const override: CameraOverride = { target: { x: 0, z: 0 }, distance: 10, pitchDeg: orbit.pitch, yawOffsetDeg: 0, blend: 6, lookHeight: 0.6, orbitRate: 9 };
  const frameIn: FrameIn = { x: 0, z: 0, midY: 0, hl: 0, hw: 0, hh: 0, heading: 0, dx: 0, dz: 0, pitchDeg: 0, fovDeg: 34, aspect: 16 / 9, insets: NO_INSETS, fill: 0.86 };
  const frameOut: FrameOut = { distance: 0, tx: 0, tz: 0, lookY: 0 };

  const ui = () => ctx.services.tryGet('ui');
  const economy = () => ctx.services.tryGet('economy');
  const garage = () => ctx.services.tryGet('garage');
  const offline = (): boolean => (economy()?.authority ?? 'local') === 'local';

  function driven(): Vehicle | null {
    const p = ctx.services.tryGet('player');
    return p && p.mode === 'driving' ? p.vehicle : null;
  }

  function bayAt(v: Vehicle, margin = 0): SprayBay | null {
    for (const b of bays) if (inBay(b, v.pos.x, v.pos.z, margin)) return b;
    return null;
  }

  /** Why Pike won't do this car now (null = he will). */
  function refusal(v: Vehicle): string | null {
    if (v.destroyed) return 'it is a wreck';
    if (!canWorkOn(v.spec)) return v.isPolice ? 'Pike won’t touch a police car' : 'it won’t fit in the bay';
    if (ctx.services.tryGet('heat')?.seen) return 'not with the police watching — lose them first';
    if (economy()?.outcomePending) return 'not now';
    if (!offline()) {
      if (!backend) return 'not open online yet';
      return backend.refuse(v);
    }
    return null;
  }

  // ---------------------------------------------------------------- mods ⇄ look

  function preview(): void {
    if (!car || !before) return;
    car.paint = pendingPaint ?? before.paint;
    putLook(car, lookWith(before.look, pending));
  }

  // ---------------------------------------------------------------- the bill

  /** Where the save keeps your car offline. */
  const keptOffline = (): string =>
    ctx.services.tryGet('save')?.hasUnlock(LOCKUP_UNLOCK_ID) ? 'kept in Lockup 7, Pilcher Ginnel' : 'kept on Halloran’s forecourt (Lockup 7 once you have the key)';
  const isMine = (v: Vehicle): boolean => (offline() ? garage()?.isMine(v) ?? false : backend?.isMine(v) ?? false);
  // Pike's prices after your standing with him (contact ladder, WP-X1); online the server applies the same.
  const paintCost = (): number => contactPrice(ctx, 'respray', offline() ? resprayCost(ctx.state.heat) : backend?.paintCost() ?? resprayCost(ctx.state.heat));
  const linePrice = (p: number): number => discounted(p, contactPerk(ctx, 'mods'));
  const keepPrice = (): number => contactPrice(ctx, 'keep', KEEP_PRICE);
  const balance = (): number => (offline() ? economy()?.balance ?? ctx.state.money : backend?.balance() ?? 0);
  const canKeep = (): boolean => !!car && !isMine(car) && (offline() ? !!garage() : !!backend);

  /** The livery on offer for the car in the bay (online, once the gang has it), or null. */
  const liveryOffer = (): { colour: number; fee: number; off: number } | null => (car && !offline() ? backend?.livery?.(car) ?? null : null);

  function order(): SprayBayOrder {
    const pc = pendingPaint !== null && before && pendingPaint !== before.paint ? paintCost() : 0;
    let mc = 0;
    for (const l of modLines(current, pending)) mc += linePrice(l.price);
    const kc = keep && canKeep() ? keepPrice() : 0;
    const lo = pending.livery !== current.livery ? liveryOffer() : null;
    const lc = lo ? (pending.livery === null ? lo.off : lo.fee) : 0;
    return { paint: pc > 0 ? pendingPaint : null, mods: { ...pending }, keep: kc > 0, paintCost: pc, modsCost: mc, keepCost: kc, liveryCost: lc, total: pc + mc + kc + lc };
  }

  const LINE_NAMES: Record<ModKey, string> = { finish: 'Finish', wheels: 'Wheels', rim: 'Rim colour', neon: 'Underglow', ride: 'Ride height', camber: 'Camber', track: 'Track width' };

  function bill(): { label: string; price: number }[] {
    const o = order();
    const out: { label: string; price: number }[] = [];
    if (o.paintCost) out.push({ label: 'Respray (new plates, heat off)', price: o.paintCost });
    for (const l of modLines(current, pending)) out.push({ label: LINE_NAMES[l.key], price: linePrice(l.price) });
    if (o.keepCost) out.push({ label: 'Keep this one (log book)', price: o.keepCost });
    if (o.liveryCost) out.push({ label: pending.livery === null ? 'Gang livery off' : 'Gang livery', price: o.liveryCost });
    return out;
  }

  // ---------------------------------------------------------------- open / close

  function open(b: SprayBay, v: Vehicle): boolean {
    const u = ui();
    if (active) return false;
    // With a page: the screen over the game (or nothing, if the UI is busy). Headless: the session alone.
    const root = ctx.uiRoot ? (ctx.uiRoot.querySelector('.bh-ui') as HTMLElement | null) : null;
    if (ctx.uiRoot && (!u || !root)) return false;
    bay = b;
    car = v;
    const look = v.data[CAR_LOOK_KEY] as CarLook | undefined;
    before = { paint: v.paint, look: look ? { ...look } : undefined };
    current = offline() ? modsFromLook(look) : backend?.modsOf(v) ?? modsFromLook(look);
    pending = { ...current };
    pendingPaint = null;
    keep = false;
    note = '';
    view = 'paint';
    aim(b, view);
    glide = false;
    if (root && u) {
      const s = new GarageModeScreen(root, controller);
      if (!u.openOverlay(s, 'garage', () => teardown(false))) {
        s.dispose();
        car = null;
        bay = null;
        before = null;
        return false;
      }
      screen = s;
      ctx.uiRoot?.classList.add('gm-open');
      u.setHudVisible(false);
      hudHidden = true;
    }
    active = true;
    armed.set(b.poi, false);
    // The premium rims' pack (rimModels.ts), so the first one tried on is the real thing, not its stand-in.
    if (ctx.render) requestRimModels();
    ctx.services.get('player').controls.acquire('sprayBay');
    ctx.input.clearTouch?.();
    v.body.setVelocity({ x: 0, z: 0 }, 0);
    views?.setActive(b.poi, true, 1);
    if (ctx.render) studioUp(v);
    ctx.services.tryGet('audio')?.ui('whoosh');
    ctx.events.emit('sprayBayOpened', { vehicleId: v.id, poi: b.poi });
    syncCamera(true);
    return true;
  }

  /** Close garage mode. `paid` = the bill was settled (the car keeps what's on it). */
  function teardown(paid: boolean, amount = 0): void {
    if (!active) return;
    active = false;
    const s = screen;
    screen = null;
    if (s) {
      ui()?.closeOverlay(s);
      s.dispose();
      ctx.uiRoot?.classList.remove('gm-open');
    }
    if (hudHidden) {
      hudHidden = false;
      ui()?.setHudVisible(true);
    }
    const v = car;
    if (v && !paid && before && ctx.services.tryGet('vehicles')?.get(v.id)) {
      v.paint = before.paint;
      putLook(v, before.look);
    }
    ctx.services.get('player').controls.release('sprayBay');
    ctx.services.tryGet('cameraRig')?.setOverride(null);
    studioDown();
    if (bay) views?.setActive(bay.poi, false, 0);
    // Offline Pike's lads turn it round to face the yard. (Online the server judges every pose: left as it is.)
    if (v && bay && paid && offline() && !v.destroyed) {
      v.body.setPose({ x: bay.center.x + bay.out.x * 0.6, z: bay.center.z + bay.out.z * 0.6 }, bay.heading);
      v.body.setVelocity({ x: 0, z: 0 }, 0);
    }
    if (v) ctx.events.emit('sprayBayClosed', { vehicleId: v.id, paid: amount });
    car = null;
    before = null;
    paying = false;
  }

  async function pay(): Promise<{ ok: boolean; reason: string | null; paid: number }> {
    const v = car;
    if (!v || !active || paying) return { ok: false, reason: 'not open', paid: 0 };
    const o = order();
    if (o.total === 0) {
      teardown(true);
      return { ok: true, reason: null, paid: 0 };
    }
    if (!offline()) {
      if (!backend) return { ok: false, reason: 'offline', paid: 0 };
      paying = true;
      screen?.render();
      const r = await backend.pay(v, o);
      paying = false;
      if (!r.ok) {
        note = r.reason ?? 'Pike shakes his head.';
        screen?.render();
        return { ok: false, reason: note, paid: 0 };
      }
      ui()?.toast(`Pike: “Lovely job.” −£${o.total.toLocaleString('en-GB')}`, 'good', 2.6);
      teardown(true, o.total);
      return { ok: true, reason: null, paid: o.total };
    }
    const eco = economy();
    if (!eco || !eco.spend(o.total, 'spray-bay')) {
      note = 'Not enough cash.';
      screen?.render();
      return { ok: false, reason: note, paid: 0 };
    }
    if (o.paint !== null) {
      v.paint = o.paint;
      eco.applyRespray(v, o.paintCost);
    }
    // Your car (or the one you just kept) goes on the save's build sheet; anything else is only on the car.
    const g = garage();
    if (g && (o.keep || g.isMine(v))) {
      const b: CarBuild = g.buildOf(v);
      b.paint = v.paint;
      if (pending.finish !== null) {
        b.metallic = pending.finish === 'metallic';
        b.matt = pending.finish === 'matt';
      }
      b.wheels = pending.wheels;
      b.rim = pending.rim;
      b.neon = pending.neon;
      b.ride = pending.ride;
      b.camber = pending.camber;
      b.track = pending.track;
      g.keep(v, b);
      if (o.keep) eco.adoptLockupCar(v);
    }
    ui()?.toast(o.keep ? `Pike: “She’s yours now.” Your car is ${keptOffline()}.` : `Pike: “Lovely job.” −£${o.total.toLocaleString('en-GB')}`, 'good', 3.2);
    teardown(true, o.total);
    return { ok: true, reason: null, paid: o.total };
  }

  // ---------------------------------------------------------------- the menu's controller

  function set(key: string, value: unknown): boolean {
    if (!car || !active || paying) return false;
    note = '';
    switch (key) {
      case 'paint':
        pendingPaint = typeof value === 'number' && before && value !== before.paint ? value : null;
        break;
      case 'finish':
        pending.finish = value === 'gloss' || value === 'metallic' || value === 'matt' ? value : null;
        break;
      case 'wheels':
        pending.wheels = typeof value === 'string' ? value : null;
        break;
      case 'rim':
        pending.rim = typeof value === 'number' ? value : null;
        break;
      case 'neon':
        pending.neon = typeof value === 'number' ? value : null;
        break;
      case 'ride':
        pending.ride = value === -1 || value === 1 ? value : 0;
        break;
      case 'camber':
        pending.camber = typeof value === 'number' ? Math.max(0, Math.min(3, Math.round(value))) : 0;
        break;
      case 'track':
        pending.track = typeof value === 'number' ? Math.max(-1, Math.min(2, Math.round(value))) : 0;
        break;
      case 'keep':
        keep = value === true && canKeep();
        break;
      case 'livery': {
        // Only the gang's colour (or none): the server puts it on from the gang's own colour anyway.
        const lo = liveryOffer();
        if (!lo) return false;
        pending.livery = value === lo.colour ? lo.colour : value === null ? null : current.livery;
        break;
      }
      default:
        return false;
    }
    preview();
    screen?.render();
    return true;
  }

  const controller: BayController = {
    get title() {
      return 'Pike’s spray bay';
    },
    get sub() {
      return `Pike · ${PIKE_LINES[(car?.id ?? 0) % PIKE_LINES.length]}`;
    },
    touch: () => touchUi.enabled,
    balance,
    car: () => car,
    current: () => current,
    pending: () => pending,
    beforePaint: () => before?.paint ?? 0,
    pendingPaint: () => pendingPaint,
    paintCost,
    ownership() {
      const v = car;
      if (!v) return { mine: false, text: '', badge: '', keepPrice: null };
      if (isMine(v)) {
        const lockup = !!ctx.services.tryGet('save')?.hasUnlock(LOCKUP_UNLOCK_ID);
        const badge = offline() ? (lockup ? 'Your car — kept in Lockup 7' : 'Your car — kept at Halloran’s') : 'Your car — kept on your account';
        return { mine: true, text: `Your car — ${offline() ? keptOffline() : backend?.keptAt() ?? 'yours'}. The work is saved.`, badge, keepPrice: null };
      }
      return { mine: false, text: 'Not yours — the work is lost when you leave this car.', badge: 'Not yours', keepPrice: canKeep() ? keepPrice() : null };
    },
    currentView: () => view,
    livery: () => liveryOffer(),
    keep: () => keep,
    bill,
    total: () => order().total,
    note: () => note,
    busy: () => paying,
    set,
    pay: () => void pay(),
    leave: () => teardown(false),
    orbit(dYaw, dPitch) {
      orbit.yaw += dYaw;
      orbit.pitch = Math.max(PITCH_MIN, Math.min(PITCH_MAX, orbit.pitch + dPitch));
      glide = false;
    },
    zoom(f) {
      orbit.zoom = Math.max(ZOOM_MIN, Math.min(ZOOM_MAX, orbit.zoom * f));
      glide = false;
    },
    resetView() {
      if (bay) aim(bay, view);
    },
    view(v) {
      if (!bay || !SHOTS[v] || v === view) return;
      view = v;
      aim(bay, v);
    },
    sound: (s) => ctx.services.tryGet('audio')?.ui(s),
  };

  /** Show the car in the dark studio instead of the yard (render/sceneOverride.ts; the city draws nothing meanwhile). */
  function studioUp(v: Vehicle): void {
    if (!ctx.render || studio) return;
    const tier = ctx.services.tryGet('renderPipeline')?.quality ?? 'low';
    studio = buildStudio(ctx.render.renderer, tier !== 'low', v.spec.length, v.spec.width);
    studio.place(v.pos.x, v.pos.z, v.heading);
    sceneOverride.scene = studio.scene;
    sceneOverride.camera = ctx.camera;
    vehicleSolo.id = v.id;
    vehicleSolo.scene = studio.scene;
  }

  /** Back to the yard: the car and the wheel batches return to the city scene on the vehicle view's next frame. */
  function studioDown(): void {
    if (!studio) return;
    if (sceneOverride.scene === studio.scene) {
      sceneOverride.scene = null;
      sceneOverride.camera = null;
    }
    vehicleSolo.id = null;
    vehicleSolo.scene = null;
    // Clearing the studio only unparents the lent car view and wheel batches (nothing of theirs is disposed);
    // the vehicle view puts them back in the city scene on its next frame.
    studio.dispose();
    studio = null;
  }

  /** Put the camera on a category's shot (it glides there; the bay's work lamps dim for the neon). */
  function aim(b: SprayBay, v: GarageView): void {
    const shot = SHOTS[v];
    // Bay terms → ground: `along` out of the mouth, `side` across.
    const dx = b.out.x * shot.along - b.out.z * shot.side;
    const dz = b.out.z * shot.along + b.out.x * shot.side;
    const len = Math.hypot(dx, dz) || 1;
    const rigYaw = ctx.services.tryGet('cameraRig')?.yaw ?? Math.PI;
    orbit.yaw = nearestTurn(orbit.yaw, yawFor(dx / len, dz / len, rigYaw));
    orbit.pitch = shot.pitch;
    orbit.zoom = 1;
    glide = true;
    if (active) views?.setActive(b.poi, true, shot.dusk ? 0.2 : 1);
  }

  /** Work-lamp level for the open bay (the neon shot wants dusk). */
  const lampWant = (): number => (active ? (SHOTS[view].dusk ? 0.15 : 1) : 0);

  /**
   * Frame the subject — the whole car, or the wheel nearest the camera — in the part of the screen the strip
   * leaves free (framing.ts), at the orbit's yaw / pitch / zoom.
   */
  function syncCamera(snap = false): void {
    const rig = ctx.services.tryGet('cameraRig');
    const v = car;
    if (!rig || !v) return;
    const yaw = rig.yaw + orbit.yaw;
    // The rig puts the camera behind screen-up: from the subject, the camera is at -forward(yaw).
    const dx = -Math.sin(yaw);
    const dz = -Math.cos(yaw);
    const spec = v.spec;
    const f = frameIn;
    f.heading = v.heading;
    f.dx = dx;
    f.dz = dz;
    f.pitchDeg = orbit.pitch;
    f.fovDeg = ctx.camera.fov;
    f.aspect = ctx.camera.aspect || 16 / 9;
    f.insets = screen?.insets() ?? NO_INSETS;
    if (SHOTS[view].subject === 'wheel') {
      // The wheel nearest the camera: ± half the wheelbase along the car, ± half the width across.
      const fx = Math.sin(v.heading);
      const fz = Math.cos(v.heading);
      const a = (fx * dx + fz * dz >= 0 ? 1 : -1) * spec.wheelbase * 0.5;
      const sd = (fz * dx - fx * dz >= 0 ? 1 : -1) * (spec.width * 0.5 - 0.12);
      f.x = v.pos.x + fx * a + fz * sd;
      f.z = v.pos.z + fz * a - fx * sd;
      // Close on the wheel, but tall enough for the whole car's height: the roof stays in the picture.
      f.midY = spec.height * 0.48;
      f.hl = 0.8;
      f.hw = 0.6;
      f.hh = spec.height * 0.5;
      f.fill = 0.92;
    } else {
      f.x = v.pos.x;
      f.z = v.pos.z;
      f.midY = spec.height * 0.48;
      f.hl = spec.length * 0.5;
      f.hw = spec.width * 0.5;
      f.hh = spec.height * 0.5;
      f.fill = 0.9;
    }
    const out = frameShot(f, orbit.zoom, frameOut);
    override.target.x = out.tx;
    override.target.z = out.tz;
    // Inside the studio's walls (radius 15 m round the car).
    override.distance = Math.min(out.distance, 12.5);
    override.lookHeight = out.lookY;
    override.pitchDeg = orbit.pitch;
    override.yawOffsetDeg = (orbit.yaw * 180) / Math.PI;
    // A category change glides (a slow, smooth swing round the car); a drag follows the finger.
    override.orbitRate = glide ? 2.8 : 9;
    override.blend = glide ? 2.8 : 6;
    rig.setOverride(override);
    if (snap) rig.snap();
  }

  // ---------------------------------------------------------------- service + system

  const service: SprayBayService = {
    get isOpen() {
      return active;
    },
    open(poiId) {
      const v = driven();
      if (!v || active) return false;
      const b = bayAt(v, 0.6) ?? null;
      if (!b || (poiId && b.poi !== poiId)) {
        ui()?.toast('Drive into the spray bay — between the low walls — and stop.', 'info', 2.6);
        return false;
      }
      const why = refusal(v);
      if (why) {
        ui()?.toast(`Pike’s spray bay: ${why}.`, 'warn', 2.6);
        return false;
      }
      return open(b, v);
    },
    prompt(poiId) {
      const v = driven();
      const b = bays.find((x) => x.poi === poiId);
      if (!v || !b) return 'Pike’s spray bay';
      if (!inBay(b, v.pos.x, v.pos.z, 0.6)) return 'Pike’s spray bay — drive in between the low walls';
      const why = refusal(v);
      return why ? `Pike’s spray bay — ${why}` : 'Pike’s spray bay — paint, wheels, neon, stance';
    },
    setBackend(b) {
      backend = b;
    },
  };

  return {
    id: 'sprayBay',
    order: 537,
    init(c) {
      ctx = c;
      c.services.provide('sprayBay', service);
      c.hooks.register('sprayBayState', () => ({
        open: active,
        bays: bays.map((b) => ({ poi: b.poi, x: b.center.x, z: b.center.z, heading: b.heading, halfL: b.halfL, halfW: b.halfW })),
        vehicleId: car?.id ?? null,
        current: { ...current },
        pending: { ...pending },
        paint: pendingPaint,
        keep,
        mine: car ? isMine(car) : null,
        bill: active ? bill() : [],
        total: active ? order().total : 0,
        note,
        orbit: { ...orbit, view },
        studio: studio !== null,
        work: workLight.level,
        driven: (() => {
          const v = driven();
          return v ? { id: v.id, paint: v.paint, look: v.data[CAR_LOOK_KEY] ?? null } : null;
        })(),
      }));
      c.hooks.register('sprayBaySet', (key, value) => set(key, value));
      c.hooks.register('rimModelsLoaded', () => rimGeometry('lattice', 'high') !== null);
      c.hooks.register('setVehicleLook', (id, look) => {
        const v = c.services.tryGet('vehicles')?.get(id);
        if (!v) return false;
        putLook(v, (look ?? undefined) as Parameters<typeof putLook>[1]);
        return true;
      });
      c.hooks.register('sprayBayPay', () => pay());
      c.hooks.register('sprayBayLeave', () => teardown(false));
      c.hooks.register('sprayBayOrbit', (dy, dp, z = 1) => {
        controller.orbit(dy, dp);
        controller.zoom(z);
      });
      c.hooks.register('sprayBayView', (v) => {
        controller.view(v);
        screen?.render();
      });
    },
    start() {
      const layout = ctx.services.tryGet('layout');
      bays = (layout?.pois ?? []).filter((p) => p.kind === 'resprayShop').map(bayFor);
      for (const b of bays) armed.set(b.poi, true);
      if (ctx.render) views = buildBayViews(ctx.scene, bays);
    },
    fixedUpdate() {
      const v = driven();
      if (active) {
        // Hold the car on the floor; the session ends if the car or the driver goes, or the police turn up.
        if (!car || v !== car || car.destroyed || economy()?.outcomePending) {
          teardown(false);
          return;
        }
        if (!paying && ctx.services.tryGet('heat')?.seen) {
          teardown(false);
          ui()?.toast('Coppers outside! Pike throws the shutter up — go!', 'bad', 2.8);
          return;
        }
        car.body.setVelocity({ x: 0, z: 0 }, 0);
        return;
      }
      if (!v) {
        for (const b of bays) armed.set(b.poi, true);
        return;
      }
      for (const b of bays) {
        const inside = inBay(b, v.pos.x, v.pos.z, -0.3);
        if (!inside) {
          if (!inBay(b, v.pos.x, v.pos.z, 1.5)) armed.set(b.poi, true);
          continue;
        }
        if (!armed.get(b.poi) || v.speed > ENTER_SPEED) continue;
        const u = ui();
        if (ctx.uiRoot && !u?.free) continue; // something else has the screen: wait
        const why = refusal(v);
        armed.set(b.poi, false);
        if (why) u?.toast(`Pike’s spray bay: ${why}.`, 'warn', 2.6);
        else open(b, v);
      }
    },
    update(dt) {
      if (active) syncCamera();
      // The work lamps (render/renderSystem.ts workLight): up while the bay is open, a cool fill from the camera's side.
      const want = lampWant();
      if (workLight.level !== want) {
        const lv = workLight.level + Math.sign(want - workLight.level) * Math.min(Math.abs(want - workLight.level), dt * 2.5);
        workLight.level = Math.abs(lv - want) < 0.01 ? want : lv;
      }
      if (active && car) {
        const cp = ctx.camera.position;
        workLight.fill.set(cp.x - car.pos.x, Math.max(4, cp.y), cp.z - car.pos.z).normalize();
      }
      views?.update(dt, ctx.services.tryGet('heat')?.level ?? 0);
      if (studio && car) {
        studio.place(car.pos.x, car.pos.z, car.heading);
        studio.setDark(SHOTS[view].dusk ? 1 : 0);
        studio.update(dt);
      }
    },
    dispose() {
      if (active) teardown(false);
      workLight.level = 0;
      views?.dispose();
      views = null;
    },
  };
}

